import KitFadeView from '@omni/kit/components/KitFadeView';
import KitText, { IKitTextProps } from '@omni/kit/components/KitText';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import { BroadcastStatus } from '@omni/kit/services/BroadcastService';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import FontSize from '@omni/kit/theming/FontSize';
import formatDuration from '@omni/kit/utilities/formatDuration';
import { transparentHexColor } from '@omni/kit/utilities/utilities';
import moment from 'moment';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

export interface LiveCountdownProps {
  broadcastStartTime: string;
  broadcastStatus: BroadcastStatus;
  /** Should handle fetching the broadcast to update the broadcast prop passed in. */
  fetchBroadcastStatus?: () => Promise<void>;
  onCountdownEnd?: () => void;
  style?: StyleProp<ViewStyle>;
  hideOnEnd?: boolean;
  showLiveOnDate?: boolean;
  showLiveStreamBeginShortly?: boolean;
  proseStyle?: StyleProp<any>;
  liveOnTextStyle?: StyleProp<any>;
  colonStyle?: StyleProp<ViewStyle>;
  numberFontSize?: number;
  labelFontSize?: number;
}

const calcTimeDiff = (startAt: string) => {
  return moment(startAt).diff(moment(), 'seconds');
};

const DEFAULT_NUMBER_FONT_SIZE = 40;
const DEFAULT_LABEL_FONT_SIZE = 14;

/**
 * This replaces the blocks version of LiveCountdown to simplify and make it
 * easier to fetch updates. With this you can simply pass the broadcast and then
 * a method to fetch the broadcast and it will handle updating that after the
 * countdown is over. If you don't pass the broadcast fetch method, you can use
 * onCountdownEnd to perform another action.
 */
export default function LiveCountdown({
  broadcastStartTime,
  broadcastStatus,
  fetchBroadcastStatus,
  onCountdownEnd,
  style,
  hideOnEnd = false,
  showLiveOnDate = true,
  showLiveStreamBeginShortly = true,
  proseStyle,
  liveOnTextStyle,
  colonStyle,
  numberFontSize,
  labelFontSize,
}: LiveCountdownProps): ReactElement {
  const CountdownEndCalled = useRef(false);
  const CountdownInterval = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { t } = useTranslation(['common', 'media']);

  const [secondsUntil, setSecondsUntil] = useState(
    calcTimeDiff(broadcastStartTime)
  );

  const [secs, mins, hours, days] = formatDuration(secondsUntil)
    .split(':')
    .reverse();
  const formattedStartAt = moment(broadcastStartTime)
    .local()
    .format('ddd, MMM D [at] h:mma');

  const hasBroadcastStarted = useMemo(() => {
    if (!broadcastStartTime) return false;

    const now = moment.utc();
    const startAt = moment.utc(broadcastStartTime);

    return now.diff(startAt, 's') >= 0;
  }, [broadcastStartTime]);

  useEffect(() => {
    if (broadcastStatus === 'scheduled' || !hasBroadcastStarted) {
      CountdownInterval.current = setInterval(() => {
        const secondsUntil = calcTimeDiff(broadcastStartTime);

        setSecondsUntil(Math.max(secondsUntil, 0));

        if (secondsUntil <= 1) {
          fetchBroadcastStatus?.();
        }

        if (!CountdownEndCalled.current && secondsUntil <= 0) {
          CountdownEndCalled.current = true;
          if (CountdownInterval.current) {
            clearInterval(CountdownInterval.current);
          }

          onCountdownEnd?.();
        }
      }, 1000);
    }

    return () => {
      if (CountdownInterval.current) {
        clearInterval(CountdownInterval.current);
      }
    };
  }, [
    broadcastStartTime,
    broadcastStatus,
    fetchBroadcastStatus,
    onCountdownEnd,
    hideOnEnd,
    hasBroadcastStarted,
  ]);

  useEffect(() => {
    if (
      broadcastStatus === 'live' &&
      hasBroadcastStarted &&
      CountdownInterval.current
    ) {
      clearInterval(CountdownInterval.current);
    }
  }, [broadcastStatus, hasBroadcastStarted]);

  return (
    <KitFadeView
      show={!hideOnEnd || secondsUntil > 0}
      style={[styles.container, style]}
    >
      {secondsUntil > 0 ? (
        <>
          {showLiveOnDate ? (
            <KitText body1 white style={[styles.liveOnText, liveOnTextStyle]}>
              {t('media:liveOnDate', { date: formattedStartAt })}
            </KitText>
          ) : null}

          <View style={styles.numberRow}>
            <NumberColumn
              number={days}
              label={t('common:day')}
              colonStyle={colonStyle}
              numberFontSize={numberFontSize}
              labelFontSize={labelFontSize}
            />
            <NumberColumn
              number={hours}
              label={t('common:hour')}
              colonStyle={colonStyle}
              numberFontSize={numberFontSize}
              labelFontSize={labelFontSize}
            />
            <NumberColumn
              number={mins}
              label={t('common:min')}
              colonStyle={colonStyle}
              numberFontSize={numberFontSize}
              labelFontSize={labelFontSize}
            />
            <NumberColumn
              number={secs}
              label={t('common:sec')}
              colonStyle={colonStyle}
              numberFontSize={numberFontSize}
              labelFontSize={labelFontSize}
              hideColon
            />
          </View>
        </>
      ) : showLiveStreamBeginShortly ? (
        <KitText
          semiBold
          fontSize={FontSize.xl}
          white
          style={[styles.prose, proseStyle]}
        >
          {t('media:liveStreamBeginShortly')}
        </KitText>
      ) : null}
    </KitFadeView>
  );
}

const NumberText = (props: IKitTextProps) => (
  <KitText extraBold white {...props} />
);
const LabelText = (props: IKitTextProps) => <KitText body1 {...props} />;

const NumberColumn = ({
  number,
  label,
  hideColon,
  colonStyle,
  numberFontSize,
  labelFontSize,
}: {
  number: string | undefined;
  label: string;
  hideColon?: boolean;
  colonStyle?: StyleProp<ViewStyle>;
  numberFontSize?: number;
  labelFontSize?: number;
}): ReactElement | null => {
  const computedNumberFontSize = numberFontSize || DEFAULT_NUMBER_FONT_SIZE;
  const computedLabelFontSize = labelFontSize || DEFAULT_LABEL_FONT_SIZE;

  return number !== undefined ? (
    <>
      <View
        style={[
          styles.numberCol,
          computedNumberFontSize < 40 ? { width: 40 } : { width: 68 },
        ]}
      >
        <NumberText
          fontSize={computedNumberFontSize}
          lineHeight={computedNumberFontSize}
        >
          {number}
        </NumberText>
        <LabelText
          fontSize={computedLabelFontSize}
          white
          style={{ opacity: 0.75 }}
        >
          {label}
        </LabelText>
      </View>
      {!hideColon ? (
        <NumberText
          fontSize={computedNumberFontSize}
          lineHeight={computedNumberFontSize}
          style={[styles.colon, colonStyle]}
        >
          :
        </NumberText>
      ) : null}
    </>
  ) : null;
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    backgroundColor: transparentHexColor(Colors.N1000, 80),
    borderRadius: BorderRadius.l,
    paddingBottom: 32,
    paddingHorizontal: 48,
    paddingTop: 24,
  },
  liveOnText: {
    marginBottom: 21,
    opacity: 0.75,
  },
  prose: {
    maxWidth: 210,
    alignSelf: 'flex-start',
  },
  numberRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
  },
  numberCol: {
    alignItems: 'center',
    flexDirection: 'column',
  },
  colon: {
    marginLeft: 10,
    marginRight: 10,
  },
});
